/**
 * keyboard-inset — publish the on-screen keyboard's overlap as a CSS variable so
 * the scroller can make room for it.
 *
 * WHY THIS IS NEEDED AT ALL, given the viewport meta already asks for
 * `interactive-widget=resizes-content`: that only governs whether the keyboard
 * resizes the LAYOUT viewport, and `app-shell` is pinned to `100svh` — the
 * SMALLEST viewport, a fixed number that by definition does not track the
 * keyboard. That pin is deliberate and must stay: `dvh` is what clipped the nav
 * after a service-worker update reload, twice, on 2026-07-27. So the shell keeps
 * its height and the keyboard simply covers the bottom of it.
 *
 * The consequence Red hit on 2026-07-29 ("the text screen has the issue where the
 * keyboard blocks part of the screen until keyboard minimised"): the Continue
 * button sits below the textarea, the keyboard covers it, and because the shell
 * is exactly viewport-height there is nothing to scroll to reach it.
 *
 * The fix is to give the scroller extra bottom padding equal to the covered
 * strip, which is what `visualViewport` reports. Then the content that was hidden
 * becomes reachable, and `scrollIntoView` on the focused field works normally.
 * Deliberately NOT a text-capture fix — every text field benefits (the transcript
 * on the save screen, the blocker, Responsible Party, feedback).
 *
 * Fails safe. No `visualViewport` (older iOS Safari) means the variable stays 0px
 * and behaviour is exactly what it is today — never worse.
 */

/** The variable the stylesheet reads. Kept in one place because a typo here is
 * silent: an unknown custom property just resolves to its fallback. */
export const KB_INSET_VAR = "--mark-kb-inset";

/** How much of the layout viewport the keyboard is covering, in CSS pixels.
 *
 * `innerHeight - (visualViewport.height + visualViewport.offsetTop)` rather than
 * the more obvious `innerHeight - visualViewport.height`: when the page is
 * scrolled or pinch-zoomed the visual viewport is OFFSET within the layout
 * viewport, and ignoring that offset reports phantom overlap with no keyboard
 * present. Clamped at 0 for the same reason.
 *
 * The 80px floor filters the small, permanent discrepancies some Android browsers
 * report from their own chrome. Without it the padding flickers on by a few
 * pixels during normal scrolling, which reads as a layout wobble. A real keyboard
 * is several hundred pixels, so the threshold is not close to anything genuine. */
export function keyboardOverlap(win: Window = window): number {
  const vv = win.visualViewport;
  if (!vv) return 0;
  const covered = win.innerHeight - (vv.height + vv.offsetTop);
  return covered > 80 ? Math.round(covered) : 0;
}

/**
 * Start publishing the overlap. Returns a teardown function.
 *
 * Writes to the ROOT element, not the shell: the shell is rendered by preact and
 * a re-render would drop an inline style set from outside, whereas
 * `documentElement` is stable for the document's life.
 */
export function trackKeyboardInset(win: Window = window): () => void {
  const vv = win.visualViewport;
  const root = win.document.documentElement;
  const apply = () => root.style.setProperty(KB_INSET_VAR, `${keyboardOverlap(win)}px`);

  apply(); // publish 0px up front so the variable always exists
  if (!vv) return () => root.style.removeProperty(KB_INSET_VAR);

  // `resize` is the keyboard opening/closing; `scroll` is the visual viewport
  // moving within the layout viewport, which changes offsetTop and therefore the
  // overlap. Both are needed.
  vv.addEventListener("resize", apply);
  vv.addEventListener("scroll", apply);
  return () => {
    vv.removeEventListener("resize", apply);
    vv.removeEventListener("scroll", apply);
    root.style.removeProperty(KB_INSET_VAR);
  };
}
